import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { SeasonPodiumEntry } from '@ottd/api';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { StatTile } from '@/components/prototype/stat-tile';
import { Button } from '@/components/ui/button';
import { formatNumber, formatSignedPoints } from '@/lib/format';

interface HeroProps {
  seasonNum: number | null;
  gamesCount: number | null;
  endsInDays: number | null;
  seasonsTotal: number | null;
  serversLive: number;
  /** Top 3 of the *current season*, not the all-time career ladder. */
  podium: SeasonPodiumEntry[];
}

export function Hero({
  seasonNum,
  gamesCount,
  endsInDays,
  seasonsTotal,
  serversLive,
  podium,
}: HeroProps) {
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');

  return (
    <section
      className="border-b-2 border-b-bev-d"
      style={{
        backgroundImage: 'var(--herograd), url(/bg/bg_1440.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        imageRendering: 'pixelated',
      }}
    >
      <div className="mx-auto grid max-w-[1280px] items-center gap-8 px-4 py-12 sm:px-5 lg:grid-cols-[1fr_420px]">
        {/* The scrim is dark in both themes, so the copy column opts out of the
            active theme's text tokens. The leaders window keeps them - a
            parchment window on the screenshot is the point of the composition. */}
        <div className="hero-scrim">
          {seasonNum ? (
            <span className="font-display border-badge-bd bg-badge-bg text-badge-tx inline-block border px-3 py-1.5 text-[11px] uppercase">
              {gamesCount
                ? t('hero.seasonBadgeGames', {
                    num: seasonNum,
                    // Raw number: the `{{games, number}}` formatter groups it.
                    games: gamesCount,
                  })
                : t('hero.seasonBadge', { num: seasonNum })}
            </span>
          ) : null}

          <h1
            className="font-display text-strong mt-4 text-3xl leading-tight sm:text-4xl"
            style={{ textShadow: 'var(--shadow-hero)' }}
          >
            {t('hero.headline1')}
            <br />
            {t('hero.headline2')}
            <br />
            <span className="text-orange">{t('hero.headline3')}</span>
          </h1>

          <p className="text-dim mt-4 max-w-md text-base">{t('hero.lede')}</p>

          <div className="mt-5 flex flex-wrap gap-2.5">
            <Button variant="primary" size="lg" asChild>
              <Link to="/register">{t('hero.cta')}</Link>
            </Button>
          </div>

          <div className="mt-7 flex flex-wrap gap-8">
            <StatTile
              boxed={false}
              value={formatNumber(gamesCount ?? 0)}
              label={t('hero.statGames')}
            />
            <StatTile
              boxed={false}
              value={formatNumber(seasonsTotal ?? 0)}
              label={t('hero.statSeasons')}
            />
            <StatTile
              boxed={false}
              value={serversLive}
              label={t('hero.statServersLive')}
              tone="green"
            />
          </div>
        </div>

        <BevelWindow
          title={
            seasonNum ? t('hero.leadersTitle', { num: seasonNum }) : t('hero.leadersTitleGeneric')
          }
          action={endsInDays !== null ? t('hero.endsIn', { count: endsInDays }) : undefined}
          shadow="lg"
        >
          {podium.length === 0 ? (
            <p className="text-dim font-mono text-sm">{t('hero.noStandings')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {podium.slice(0, 3).map((row, index) => (
                <li
                  key={row.rank}
                  className={
                    index === 0
                      ? 'bg-panel-in border-orange flex items-center gap-2.5 border-2 p-2'
                      : 'border-line flex items-center gap-2.5 border-b p-2 last:border-b-0'
                  }
                >
                  <span
                    className={`font-display text-sm ${index === 0 ? 'text-orange' : 'text-dim'}`}
                  >
                    {row.rank}
                  </span>
                  <PlayerAvatar name={row.name} src={row.avatar} size={index === 0 ? 28 : 24} />
                  <Link
                    to={`/player/${encodeURIComponent(row.name)}`}
                    className="text-strong flex-1 truncate font-semibold"
                  >
                    {row.name}
                  </Link>
                  <span className={`font-mono text-sm ${index === 0 ? 'text-gold' : 'text-green'}`}>
                    {formatSignedPoints(row.points)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {/* The podium above is one season's standings, so the link has to
              land on that season's ladder - /player/top is the all-time one.
              Without a season (pre-load) the all-time ladder is the only
              sensible destination. */}
          <Link
            to={seasonNum ? `/season/${seasonNum}` : '/player/top'}
            className="font-display text-orange mt-2 flex items-center justify-center gap-1 border-t border-line pt-2 text-[10px] uppercase"
          >
            {seasonNum ? t('hero.viewSeasonTop100', { num: seasonNum }) : tc('viewTop100')}
            <ArrowRight className="size-3" aria-hidden />
          </Link>
        </BevelWindow>
      </div>
    </section>
  );
}
